import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MetaAppFormData, AppCapability } from '../types/meta.types';
import { InfoIcon, Lock, Globe, FlaskConical, Rocket, Send, Workflow, UserCircle2, MessageSquare } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
// Select removido após mudança pra multi-select de projects (checkbox list).

interface ProjectOption {
    id: number;
    name: string;
}

interface CreateEditAppDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    formData: MetaAppFormData;
    onFormChange: (data: MetaAppFormData) => void;
    onSubmit: (e: React.FormEvent) => void;
    isLoading: boolean;
    isEdit?: boolean;
    /** Projetos disponíveis pra vincular o app.
     *  Backend já retorna filtrado pelos projetos que o user atual tem acesso. */
    availableProjects?: ProjectOption[];
    /** Se false, esconde a opção "Público" — non-admin é obrigado a vincular
     *  o app a pelo menos 1 projeto. Default: true (admin). */
    canCreatePublic?: boolean;
}

export default function CreateEditAppDialog({
                                                open,
                                                onOpenChange,
                                                formData,
                                                onFormChange,
                                                onSubmit,
                                                isLoading,
                                                isEdit = false,
                                                availableProjects,
                                                canCreatePublic = true,
                                            }: CreateEditAppDialogProps) {
    // Callback é fixo, derivado da URL da API. Não tem por que ser editável —
    // o backend é quem trata o callback e o user tem que copiar essa URL exata
    // pro Facebook Developers.
    const apiBase = (import.meta.env.VITE_META_API_URL || '').replace(/\/$/, '');
    const redirectUri = apiBase
        ? `${apiBase}/meta/oauth/callback`
        : 'http://localhost:8000/api/v1/meta/oauth/callback';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        {isEdit ? 'Editar App' : 'Criar App Personalizada'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        {isEdit
                            ? 'Atualize as informações da sua aplicação Meta.'
                            : 'Configure uma nova aplicação do Meta para autenticação personalizada.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">

                {/* Guia de Configuração */}
                {!isEdit && (
                    <Alert className="border-[#7dd3fc] bg-[#7dd3fc]/5">
                        <InfoIcon className="h-4 w-4 text-[#0f172a]" />
                        <AlertDescription className="text-sm text-gray-700">
                            <strong className="font-semibold">Como obter as credenciais:</strong>
                            <ol className="mt-2 ml-4 list-decimal space-y-1">
                                <li>
                                    Acesse{' '}
                                    <a
                                        href="https://developers.facebook.com/apps"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#0f172a] hover:underline font-medium"
                                    >
                                        Facebook Developers
                                    </a>
                                </li>
                                <li>Crie uma nova aplicação ou selecione uma existente</li>
                                <li>Vá em "Configurações" → "Básico" para obter App ID e Secret</li>
                                <li>
                                    Configure a URL de callback abaixo nas configurações do Facebook Login
                                </li>
                            </ol>
                        </AlertDescription>
                    </Alert>
                )}

                    {/* Nome da App */}
                    <div className="space-y-2">
                        <Label htmlFor="app_name" className="text-gray-700 font-medium">
                            Nome da App <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="app_name"
                            value={formData.app_name}
                            onChange={(e) =>
                                onFormChange({ ...formData, app_name: e.target.value })
                            }
                            placeholder="Minha App Personalizada"
                            required
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a]"
                        />
                    </div>

                    {/* App ID */}
                    <div className="space-y-2">
                        <Label htmlFor="app_id" className="text-gray-700 font-medium">
                            App ID <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="app_id"
                            value={formData.app_id}
                            onChange={(e) =>
                                onFormChange({ ...formData, app_id: e.target.value })
                            }
                            placeholder="123456789012345"
                            required
                            disabled={isEdit}
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono"
                        />
                        <p className="text-xs text-gray-500">
                            Encontrado em: Configurações → Básico
                        </p>
                    </div>

                    {/* App Secret */}
                    <div className="space-y-2">
                        <Label htmlFor="app_secret" className="text-gray-700 font-medium">
                            App Secret <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="app_secret"
                            type="password"
                            value={formData.app_secret}
                            onChange={(e) =>
                                onFormChange({ ...formData, app_secret: e.target.value })
                            }
                            placeholder="••••••••••••••••••••••••••••••••"
                            required
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono"
                        />
                        <p className="text-xs text-gray-500">
                            Clique em "Mostrar" ao lado do App Secret para visualizar
                        </p>
                    </div>

                    {/* Redirect URI — fixo, exibido apenas pra cópia */}
                    <div className="space-y-2">
                        <Label className="text-gray-700 font-medium">URL de Callback</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                value={redirectUri}
                                readOnly
                                tabIndex={-1}
                                className="border-gray-200 bg-gray-50 text-gray-700 font-mono text-sm cursor-default"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => navigator.clipboard?.writeText(redirectUri)}
                                className="shrink-0 border-gray-300"
                            >
                                Copiar
                            </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                            Cole esta URL em: Produtos → Facebook Login → Configurações → URLs de redirecionamento válidas do OAuth
                        </p>
                    </div>

                    {/* API Version */}
                    <div className="space-y-2">
                        <Label htmlFor="api_version" className="text-gray-700 font-medium">
                            Versão da API
                        </Label>
                        <Input
                            id="api_version"
                            value={formData.api_version}
                            onChange={(e) =>
                                onFormChange({ ...formData, api_version: e.target.value })
                            }
                            placeholder="v18.0"
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono"
                        />
                        <p className="text-xs text-gray-500">Padrão: v18.0</p>
                    </div>

                    {/* Visibilidade — quem enxerga/usa o app */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-4 space-y-3">
                        <div>
                            <Label className="text-gray-700 font-semibold text-sm">Visibilidade</Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Define quem pode ver e usar este app — vínculo a um ou mais projetos do apisistema.
                            </p>
                        </div>
                        <div className={canCreatePublic ? 'grid grid-cols-2 gap-3' : ''}>
                            {canCreatePublic && (
                                <button
                                    type="button"
                                    onClick={() => onFormChange({
                                        ...formData,
                                        is_private: false,
                                        project_id: null,
                                        project_ids: [],
                                    })}
                                    className={`text-left p-3 rounded-lg border-2 transition-all bg-white ${
                                        !formData.is_private
                                            ? 'border-emerald-500 ring-2 ring-emerald-100'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Globe className="h-4 w-4 text-emerald-600" />
                                        <span className="text-sm font-semibold text-gray-900">Público</span>
                                    </div>
                                    <p className="text-xs text-gray-500">Visível em todos os projetos.</p>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => onFormChange({ ...formData, is_private: true })}
                                className={`text-left p-3 rounded-lg border-2 transition-all bg-white ${
                                    formData.is_private
                                        ? 'border-violet-500 ring-2 ring-violet-100'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Lock className="h-4 w-4 text-violet-600" />
                                    <span className="text-sm font-semibold text-gray-900">Vinculado a projeto(s)</span>
                                </div>
                                <p className="text-xs text-gray-500">Só users com acesso a algum dos projetos enxergam.</p>
                            </button>
                        </div>

                        {/* Projects multi-select — quando privado */}
                        {formData.is_private && (
                            <div className="space-y-2 pt-2 pl-3 border-l-2 border-violet-200">
                                <Label className="text-gray-700 font-medium text-xs uppercase tracking-wider">
                                    Vincular aos projetos <span className="text-red-500">*</span>
                                    {(formData.project_ids?.length ?? 0) > 0 && (
                                        <span className="ml-2 normal-case text-gray-500 font-normal">
                                            ({formData.project_ids!.length} selecionado{formData.project_ids!.length > 1 ? 's' : ''})
                                        </span>
                                    )}
                                </Label>
                                {availableProjects && availableProjects.length > 0 ? (
                                    <>
                                        <div className="border rounded-lg max-h-44 overflow-y-auto bg-white">
                                            {availableProjects.map((p) => {
                                                const checked = (formData.project_ids ?? []).includes(p.id);
                                                return (
                                                    <label
                                                        key={p.id}
                                                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => {
                                                                const current = formData.project_ids ?? [];
                                                                const next = checked
                                                                    ? current.filter((x) => x !== p.id)
                                                                    : [...current, p.id];
                                                                onFormChange({
                                                                    ...formData,
                                                                    project_ids: next,
                                                                    // Sincroniza legacy project_id pro primeiro selecionado
                                                                    project_id: next[0] ?? null,
                                                                });
                                                            }}
                                                        />
                                                        <span className="text-sm">{p.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-1">
                                            Selecione 1 ou mais. O app fica visível pra users que tenham acesso a QUALQUER um deles.
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-xs text-gray-500 italic">
                                        Nenhum projeto disponível. Crie um projeto no apisistema primeiro.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Modo do app no Meta — orthogonal à visibilidade */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-4 space-y-3">
                        <div>
                            <Label className="text-gray-700 font-semibold text-sm">Modo do app no Meta</Label>
                            <p className="text-xs text-gray-500 mt-0.5">Status do app no Facebook Developers — não afeta visibilidade.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => onFormChange({ ...formData, meta_mode: 'dev' })}
                                className={`text-left p-3 rounded-lg border-2 transition-all bg-white ${
                                    formData.meta_mode === 'dev'
                                        ? 'border-amber-500 ring-2 ring-amber-100'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <FlaskConical className="h-4 w-4 text-amber-600" />
                                    <span className="text-sm font-semibold text-gray-900">Dev</span>
                                </div>
                                <p className="text-xs text-gray-500">App em desenvolvimento — só testers conseguem usar.</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => onFormChange({ ...formData, meta_mode: 'production' })}
                                className={`text-left p-3 rounded-lg border-2 transition-all bg-white ${
                                    formData.meta_mode === 'production'
                                        ? 'border-blue-500 ring-2 ring-blue-100'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Rocket className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-semibold text-gray-900">Production</span>
                                </div>
                                <p className="text-xs text-gray-500">App publicado — qualquer usuário pode conectar.</p>
                            </button>
                        </div>
                    </div>

                    {/* Capacidades suportadas — herdadas por todas as páginas conectadas a esse app */}
                    <CapabilitiesSection formData={formData} onFormChange={onFormChange} />
                </div>

                <DialogFooter className="gap-2 sm:gap-0 px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50/30">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                        className="border-gray-300"
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        disabled={isLoading}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        {isLoading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                {isEdit ? 'Salvando...' : 'Criando...'}
                            </>
                        ) : isEdit ? (
                            'Salvar Alterações'
                        ) : (
                            'Criar App'
                        )}
                    </Button>
                </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ─── CapabilitiesSection ─────────────────────────────────────────────
//
// Toggles que declaram quais capabilities o app suporta. Herdado por todos
// os vínculos página↔app no resolver do ms-meta-flow.
//
// Uso prático: você cria o app, marca "Moderar comentários" se concedeu a
// permission `pages_manage_engagement` no Meta Dev Console. Quando uma
// página é conectada a esse app, ela já herda essa capability — sem
// precisar reconfigurar página por página.

const CAPABILITY_DEFS: Array<{
    key: AppCapability;
    label: string;
    description: string;
    metaPermission: string;
    icon: React.ComponentType<{ className?: string }>;
}> = [
    {
        key: 'broadcast',
        label: 'Enviar broadcasts',
        description: 'Disparar mensagens em massa pra leads desse app.',
        metaPermission: 'pages_messaging',
        icon: Send,
    },
    {
        key: 'flow',
        label: 'Disparar fluxos',
        description: 'Receber mensagens e processar gatilhos de funis.',
        metaPermission: 'pages_messaging + pages_manage_metadata',
        icon: Workflow,
    },
    {
        key: 'lead_profile',
        label: 'Sincronizar perfil do lead',
        description: 'Buscar nome, foto e dados do lead via Graph API.',
        metaPermission: 'pages_user_profile_pic + pages_messaging',
        icon: UserCircle2,
    },
    {
        key: 'comment_moderation',
        label: 'Moderar comentários',
        description: 'Curtir, esconder, deletar e responder comentários.',
        metaPermission: 'pages_manage_engagement',
        icon: MessageSquare,
    },
];

interface CapabilitiesSectionProps {
    formData: MetaAppFormData;
    onFormChange: (data: MetaAppFormData) => void;
}

function CapabilitiesSection({ formData, onFormChange }: CapabilitiesSectionProps) {
    const selected = new Set<AppCapability>(formData.capabilities ?? []);

    const toggle = (cap: AppCapability) => {
        const next = new Set(selected);
        if (next.has(cap)) next.delete(cap); else next.add(cap);
        // Preserva a ordem canônica das definições — facilita debug + diff
        const ordered = CAPABILITY_DEFS.map(d => d.key).filter(k => next.has(k));
        onFormChange({ ...formData, capabilities: ordered });
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-4 space-y-3">
            <div>
                <Label className="text-gray-700 font-semibold text-sm">Capacidades suportadas</Label>
                <p className="text-xs text-gray-500 mt-0.5">
                    Marque o que esse app pode fazer no Meta. Herdado por TODAS as páginas conectadas a ele.
                </p>
            </div>
            <div className="space-y-2">
                {CAPABILITY_DEFS.map(({ key, label, description, metaPermission, icon: Icon }) => {
                    const isOn = selected.has(key);
                    return (
                        <button
                            type="button"
                            key={key}
                            onClick={() => toggle(key)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all bg-white flex items-start gap-3 ${
                                isOn
                                    ? 'border-blue-500 ring-2 ring-blue-100'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <div className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${
                                isOn ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                            }`}>
                                {isOn && (
                                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <Icon className={`h-4 w-4 shrink-0 ${isOn ? 'text-blue-600' : 'text-gray-500'}`} />
                                    <span className="text-sm font-semibold text-gray-900">{label}</span>
                                </div>
                                <p className="text-xs text-gray-500 leading-snug">{description}</p>
                                <p className="text-[10px] text-gray-400 mt-1 font-mono">
                                    Meta permission: {metaPermission}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
            {selected.size === 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    ⚠ Sem capacidades marcadas, o app cai no fallback legado (production = tudo, dev = broadcast+flow).
                </p>
            )}
        </div>
    );
}